import {Box, Button, DialogActions, DialogContent, List, ListItem, ListItemIcon, ListItemText, TextField, useMediaQuery, useTheme, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader} from "../IntegrationDesign";
import {Check} from "@mui/icons-material";
import {integrationActions} from "../../../../store/actions/integrationAction";
import {connect} from "react-redux";
import React, {useCallback, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import Spinner from "../../../spinner/Spinner";
import {integrationService} from "../../../../service/integrationService";
import {alertActions} from "../../../../store/actions/alertActions";
import useCustomStyles from "../../../utils/UseCustomStyles";
import {useParams} from "react-router-dom";

const customStyles = (theme) => ({
    root: {
        paddingTop: 8,
        paddingLeft: 15,
        paddingRight: 15,
        paddingBottom: 15
    },
    title: {
        textAlign: "center",
        paddingBottom: 0
    },
    description: {
        color: theme.palette.text.secondary
    },
    chooseAccount: {
        fontWeight: 500,
        fontSize: 14,
        paddingTop: 24,
        paddingBottom: 4
    },
    adAccountList: {
        maxHeight: 500,
        overflow: "auto",
        paddingBottom: 8,
        marginBottom: 32
    },
    adAccountListItem: {
        paddingBottom: 16
    },
    listItem: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: 0
    },
    listItemIcon: {
        minWidth: 42
    },
    listItemText: {
        whiteSpace: "normal"
    },
    infoAlert: {
        marginTop: 12
    },
    logo: {
        backgroundColor: theme.palette.background.default,
        borderRadius: 4,
        padding: "6px 16px"
    }
});

function DomainCloudflareIntegration(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {integration, create, error, update, alertState} = props;

    const params = useParams();
    const integrationIdParam = params?.id;

    const {register, handleSubmit, setValue, formState: {errors}} = useForm();

    const [loading, setLoading] = useState(false);

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleOnSubmit = useCallback((data) => {
        if (integrationIdParam > 0) {
            update(integrationIdParam, data);
        } else {
            create(data);
        }
    });

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        props.reset();
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [props]);

    useEffect(() => {
        async function find(id): void {
            setLoading(true);
            await integrationService.findIntegration(id).then((result) => {
                setValue("name", result?.networkName);
                result?.integrationCredentials?.forEach((c) => {
                    if (c?.keyName === "server_cloudflare_api_token") {
                        setValue("apiToken", c?.keyValue);
                    }
                });
                setLoading(false);
            }).catch((e) => {
                setLoading(false);
                error(e?.response?.data?.error);
                props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
            });
        }

        if (integrationIdParam > 0) {
            find(integrationIdParam);
        }
    }, [create, error, integrationIdParam, props.history, props.location, setValue]);

    useEffect(() => {
        const listener = event => {
            if (event?.code === "Enter" || event?.code === "NumpadEnter") {
                event.preventDefault();
                handleSubmit(handleOnSubmit)();
            }
        };
        document.addEventListener("keydown", listener);
        return () => {
            document.removeEventListener("keydown", listener);
        };
    }, [handleOnSubmit, handleSubmit]);

    useEffect(() => {
        if (alertState?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alertState?.successMsg, handleClose])

    return (

        <Dialog open={true}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="cloudflare-integration-form"
        >
            <IntegrationHeader name="Cloudflare" logo="/assets/images/servers/cloudflare.svg" id="cloudflare-integration-form"/>
            <DialogContent>
                <div className="integration-body">

                    {(integration.loading.integration || loading) && <Spinner size={32} overComponentBox={true}/>}

                    <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete={"off"}>
                        <Box display="flex" flexWrap={"wrap"}>
                            <Box py={0.75} className={classes?.description}>
                                <p>
                                    Please see our integration
                                    guide <Link target="_blank" underline="hover" rel="noreferrer"
                                                href="https://skro.eu/docs/deploy-website/cloudflare-integration"
                                >
                                    here
                                </Link>
                                </p>
                            </Box>
                            <Box py={0.75} width="100%">
                                <TextField
                                    error={!!errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    id="name"
                                    label="Integration Name*"
                                    {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                    name="name"
                                    size="small"
                                    type="text"
                                    placeholder="Enter your integration name"
                                    fullWidth={true}
                                    defaultValue="Cloudflare API"
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box py={0.75} width="100%">
                                <TextField
                                    error={!!errors.apiToken}
                                    variant="outlined"
                                    margin="dense"
                                    id="apiToken"
                                    label="Cloudflare API Token*"
                                    {...register("apiToken", {required: true, maxLength: 255, minLength: 1})}
                                    name="apiToken"
                                    size="small"
                                    type="text"
                                    placeholder="Enter your API Token"
                                    fullWidth={true}
                                    defaultValue=""
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </form>

                    <Box display="flex" flexDirection="row">
                        <Box p={0} width="100%">
                            <List>
                                <ListItem className={classes?.listItem}>
                                    <ListItemIcon className={classes?.listItemIcon}>
                                        <Check color={"primary"}/>
                                    </ListItemIcon>
                                    <ListItemText classes={{primary: classes?.listItemText}}>
                                        Verify domain for SSL certificate.
                                    </ListItemText>
                                </ListItem>
                                <ListItem className={classes?.listItem}>
                                    <ListItemIcon className={classes?.listItemIcon}>
                                        <Check color={"primary"}/>
                                    </ListItemIcon>
                                    <ListItemText classes={{primary: classes?.listItemText}}>
                                        Create a CNAME record for website distribution.
                                    </ListItemText>
                                </ListItem>
                            </List>
                        </Box>
                    </Box>
                </div>
            </DialogContent>
            <DialogActions>
                <Box display="flex" flexWrap="noWrap">
                    <Box mr={2}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            {integrationIdParam > 0 ? "Cancel" : "Close"}
                        </Button>
                    </Box>
                    <Box>
                        <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                            Save & Close
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alertState: state.alert
    };
};

const mapDispatchToProps = {
    reset: integrationActions.reset,
    create: integrationActions.createDomainCloudflareIntegration,
    update: integrationActions.updateDomainCloudflareIntegration,
    error: alertActions.error
};

export default connect(mapStateToProps, mapDispatchToProps)(DomainCloudflareIntegration);
